import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import AnalysisForm from './AnalysisForm';
import AnalysisResults from '../components/analysis/AnalysisResults';
import Logout from '../components/auth/Logout';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Upload from '../components/Upload';

const DashboardContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 100%;
  background: var(--background-gradient);
  color: var(--text-color);
  font-family: 'Poppins', sans-serif;
  position: relative;
  overflow-x: hidden;
  
  &::before {
    content: "";
    position: absolute;
    width: 15px;
    height: 15px;
    border-radius: 50%;
    background-color: var(--accent-color);
    top: 15%;
    left: 10%;
    opacity: 0.6;
    animation: float 5s infinite ease-in-out;
    z-index: 1;
    pointer-events: none;
  }
  
  &::after {
    content: "";
    position: absolute;
    width: 25px;
    height: 25px;
    border-radius: 50%;
    background-color: var(--primary-color);
    bottom: 20%;
    right: 15%;
    opacity: 0.4;
    animation: float 7s infinite ease-in-out;
    z-index: 1;
    pointer-events: none;
  }
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  position: absolute;
  top: 20px;
  right: 20px;
`;

const UserAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: var(--primary-color);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  color: white;
  box-shadow: 0 4px 10px rgba(255, 64, 192, 0.4);
`;

const UserName = styled.span`
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
`;

const UserCredits = styled.span`
  color: var(--secondary-color);
  font-weight: 600;
  font-size: 0.9rem;
  margin-left: 10px;
  padding: 5px 15px;
  background-color: rgba(0,0,0,0.3);
  border-radius: 20px;
  border: 1px solid var(--secondary-color);
  box-shadow: 0 3px 10px rgba(255, 208, 0, 0.2);
`;

const MainContent = styled.main`
  flex: 1;
  padding: 20px;
  margin-top: 40px;
  max-width: 1200px;
  width: 100%;
  margin-left: auto;
  margin-right: auto;
  position: relative;
  z-index: 2;
`;

const WelcomeSection = styled.div`
  background-color: rgba(0, 0, 0, 0.25);
  border-radius: 25px;
  padding: 35px;
  margin-bottom: 40px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  position: relative;
  
  &::before {
    content: "😍";
    position: absolute;
    top: -15px;
    right: 30px;
    font-size: 35px;
    filter: drop-shadow(0 3px 5px rgba(0,0,0,0.3));
    animation: float 3s infinite ease-in-out;
  }
  
  &::after {
    content: "💘";
    position: absolute;
    bottom: -15px;
    left: 40px;
    font-size: 28px;
    filter: drop-shadow(0 3px 5px rgba(0,0,0,0.3));
    animation: float 4s infinite ease-in-out;
    animation-delay: 1s;
  }
`;

const WelcomeTitle = styled.h2`
  font-family: 'Bubblegum Sans', 'Poppins', sans-serif;
  font-size: 2.2rem;
  margin-bottom: 15px;
  color: var(--primary-color);
  text-shadow: 0 2px 10px rgba(255, 64, 192, 0.3);
`;

const WelcomeText = styled.p`
  color: var(--text-secondary);
  line-height: 1.7;
  margin-bottom: 25px;
  font-size: 1.05rem;
`;

const HistoryLink = styled(Link)`
  display: inline-block;
  padding: 12px 25px;
  background-color: var(--secondary-color);
  color: #170032;
  text-decoration: none;
  border-radius: 30px;
  font-weight: 600;
  transition: all 0.3s ease;
  box-shadow: 0 5px 15px rgba(255, 208, 0, 0.3);
  
  &:hover {
    background-color: #FFE03D;
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(255, 208, 0, 0.4);
  }
  
  i {
    margin-left: 8px;
  }
`;

// Contenitore principale per la forma di analisi e i risultati
const AnalysisContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

// Toast notification component
const Toast = styled.div`
  position: fixed;
  top: 80px;
  right: 20px;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 15px 25px;
  border-radius: 10px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3), 0 0 10px rgba(255, 208, 0, 0.5);
  z-index: 1000;
  display: flex;
  align-items: center;
  gap: 12px;
  border-left: 5px solid #FFD000;
  animation: slideInRight 0.5s forwards, fadeOut 0.5s 4.5s forwards;
  font-weight: 600;
  
  @keyframes slideInRight {
    from { transform: translateX(100%); opacity: 0; }
    to { transform: translateX(0); opacity: 1; }
  }
  
  @keyframes fadeOut {
    from { opacity: 1; }
    to { opacity: 0; }
  }
  
  i {
    color: var(--secondary-color);
    font-size: 1.2rem;
  }
`;

const Dashboard = () => {
  const { currentUser } = useAuth();
  const [showUpload, setShowUpload] = useState(true);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [openCreditsModal, setOpenCreditsModal] = useState(false);
  const navbarRef = useRef(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  
  // Stato dell'analisi sollevato al livello del componente Dashboard
  const [analysisState, setAnalysisState] = useState({
    loading: false,
    results: '',
    analysisError: '',
    showResults: false
  });

  // Check if credits were added (redirected from payment success)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const fromPayment = params.get('from') === 'payment';
    const planId = params.get('plan');
    
    if (fromPayment && planId) {
      const creditAmounts = {
        'basic': 500,
        'standard': 1500,
        'premium': 3000
      };
      
      const credits = creditAmounts[planId] || 0;
      
      if (credits > 0) {
        setToastMessage(`${credits} credits have been added to your account balance!`);
        setShowToast(true);
        
        // Nascondi il toast dopo 5 secondi
        setTimeout(() => {
          setShowToast(false);
        }, 5000);
        
        // Pulisci l'URL
        navigate('/dashboard', { replace: true });
      }
    }
  }, [location, navigate]);

  // Check if we need to open the credits modal (redirected from payment cancellation)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('openCreditsModal') === 'true') {
      // Troviamo il componente navbar e chiamiamo la funzione per aprire il modal
      // Usando un piccolo timeout per assicurarci che tutto sia montato correttamente
      setTimeout(() => {
        const addFundsButton = document.querySelector('.add-funds-button');
        if (addFundsButton) {
          addFundsButton.click();
        }
      }, 500);
      
      // Clean up the URL
      window.history.replaceState({}, document.title, '/dashboard');
    }
  }, [location]);

  // Salviamo i risultati dell'analisi in localStorage per evitare di perderli su refresh
  useEffect(() => {
    // Recuperiamo i risultati salvati se esistono e solo se c'è un utente autenticato
    const savedResults = localStorage.getItem('analysisResults');
    if (savedResults && currentUser) {
      try {
        const savedState = JSON.parse(savedResults);
        console.log("Dashboard: Retrieved saved results:", savedState);
        setAnalysisState(savedState);
      } catch (e) {
        console.error("Dashboard: Error parsing saved results:", e);
        localStorage.removeItem('analysisResults');
      }
    } else if (!currentUser && savedResults) {
      // Se non c'è un utente autenticato ma ci sono risultati salvati, li rimuoviamo
      console.log("Dashboard: No authenticated user, removing saved results");
      localStorage.removeItem('analysisResults');
    }
  }, [currentUser]);

  // Reset dello stato delle analisi quando l'utente cambia (logout/login)
  useEffect(() => {
    if (!currentUser) {
      console.log("Dashboard: User logged out, resetting analysis state");
      setAnalysisState({
        loading: false,
        results: '',
        analysisError: '',
        showResults: false
      });
    }
  }, [currentUser]);

  // Funzione di callback per l'aggiornamento dello stato dell'analisi
  const handleAnalysisStateChange = (newState) => {
    console.log("Dashboard: Updating analysis state:", newState);
    
    // Se è una funzione, la eseguiamo passando lo stato corrente
    if (typeof newState === 'function') {
      setAnalysisState(prevState => {
        const updatedState = newState(prevState);
        console.log("Dashboard: State updated using function to:", updatedState);
        
        // Salviamo i risultati in localStorage se ci sono
        if (updatedState.results && !updatedState.loading) {
          localStorage.setItem('analysisResults', JSON.stringify(updatedState));
          console.log("Dashboard: Results saved to localStorage");
          
          // Salva i risultati nella cronologia dell'utente attraverso l'API
          saveAnalysisToHistory(updatedState);
        }
        
        return updatedState;
      });
    } else {
      // Altrimenti aggiorniamo direttamente con il nuovo stato
      setAnalysisState(newState);
      
      // Salviamo i risultati in localStorage se ci sono
      if (newState.results && !newState.loading) {
        localStorage.setItem('analysisResults', JSON.stringify(newState));
        console.log("Dashboard: Results saved to localStorage");
        
        // Salva i risultati nella cronologia dell'utente attraverso l'API
        saveAnalysisToHistory(newState);
      }
    }
  };
  
  // Funzione per salvare l'analisi nella cronologia
  const saveAnalysisToHistory = async (analysisState) => {
    // Verifica se ci sono condizioni valide per salvare l'analisi
    if (!currentUser || !analysisState.results || analysisState.loading) {
      console.log("Dashboard: Non salvo l'analisi - condizioni di base non soddisfatte");
      return;
    }
    
    // Controlli aggiuntivi per evitare il salvataggio di analisi vuote o non valide
    const analysisText = analysisState.results.trim();
    if (analysisText.length < 50) {
      console.log("Dashboard: Analysis too short or empty, not saving to history");
      return; // Non salvare analisi troppo brevi o vuote
    }
    
    // Verifico se il testo dell'analisi contiene contenuto significativo
    // non solo placeholder o messaggi di errore
    if (analysisText.includes("N/A") || 
        analysisText.includes("Date not available") ||
        analysisText.toLowerCase().includes("error") ||
        analysisText.toLowerCase().includes("undefined")) {
      console.log("Dashboard: Analysis contains placeholder or error text, not saving to history");
      return;
    }
    
    // Verifica se esistono già analisi con lo stesso contenuto per evitare duplicati
    const existingAnalyses = currentUser.analysis_history || [];
    const isDuplicate = existingAnalyses.some(analysis => 
      analysis.result && analysis.result.trim() === analysisText
    );
    
    if (isDuplicate) {
      console.log("Dashboard: Duplicate analysis detected, not saving to history");
      return; // Non salvare analisi duplicate
    }
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error("Dashboard: No token found, cannot save to history");
        return;
      }
      
      // Recupera informazioni sulle immagini dal localStorage se disponibili
      const imageInfo = {
        count: parseInt(localStorage.getItem('analysisImagesCount') || '0'),
        filenames: JSON.parse(localStorage.getItem('analysisImageFilenames') || '[]')
      };
      
      // Non salvare se non ci sono immagini associate
      if (imageInfo.count === 0 || imageInfo.filenames.length === 0) {
        console.log("Dashboard: No images associated with analysis, not saving to history");
        return;
      }
      
      // Prepara i parametri dell'analisi
      const parameters = {
        context: localStorage.getItem('analysisContext') || 'general',
        specific_goal: localStorage.getItem('analysisGoal') || ''
      };
      
      console.log("Dashboard: Saving valid analysis to history");
      
      const response = await fetch('https://api.instarizz.me/api/analysis/history', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          context: parameters.context,
          specific_goal: parameters.specific_goal,
          results: analysisState.results,
          image_summary: imageInfo,
          parameters: parameters,
          cost: 1,
          timestamp: new Date().toISOString() // Aggiungi un timestamp ISO valido
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log("Dashboard: Analysis saved to history:", data);
      } else {
        console.error("Dashboard: Failed to save to history:", await response.text());
      }
    } catch (error) {
      console.error("Dashboard: Error saving to history:", error);
    }
  };

  // Debug: monitoriamo i cambiamenti dello stato dell'analisi
  useEffect(() => {
    console.log("Dashboard: Analysis state changed:", analysisState);
  }, [analysisState]);
  
  return (
    <DashboardContainer>
      {showToast && (
        <Toast>
          <i className="fas fa-coins"></i>
          {toastMessage}
        </Toast>
      )}
      
      <MainContent>
        <WelcomeSection>
          <WelcomeTitle>
            {currentUser ? `Hi ${currentUser.name}! Ready to analyze?` : 'Welcome to InstaRizz!'}
          </WelcomeTitle>
          <WelcomeText>
            From here you can analyze Instagram profiles, view your past analyses, 
            and manage your account settings. Start by uploading profile screenshots below.
          </WelcomeText>
          <HistoryLink to="/history">
            View Analysis History
            <i className="fas fa-history"></i>
          </HistoryLink>
        </WelcomeSection>
        
        {/* Contenitore che include sia il form che i risultati */}
        <AnalysisContainer>
          {/* Passa lo stato e la funzione di callback a AnalysisForm */}
          <AnalysisForm 
            onAnalysisStateChange={handleAnalysisStateChange} 
            analysisState={analysisState}
          />
          
          {/* Componente separato per la visualizzazione dei risultati */}
          <AnalysisResults 
            loading={analysisState.loading}
            results={analysisState.results}
            analysisError={analysisState.analysisError}
            visible={analysisState.showResults}
          />
        </AnalysisContainer>
      </MainContent>
    </DashboardContainer>
  );
};

export default Dashboard; 